import { useMarketManagers } from 'typescript/context/market_managers';

// These are just some standard props you can configure on the market toast
// if more things need to be added like inline links or buttons, we can add them here as potential props
// https://market-storybook.s3.amazonaws.com/7.0.0/index.html?path=/docs/components-toast-toaster-toast-api--docs
type Toast = {
  message?: string;
  persistent?: HTMLMarketToastElement['persistent'];
  variant?: HTMLMarketToastElement['variant'];
  actionLinks?: Array<{
    href: string;
    text: string;
    onClick?: () => void;
  }>;
};

/**
 * Hook to help display a Market Toast
 * @returns a function `showToast` which can be called to show a market-toast
 * @example
 * const { showToast } = useToaster();
 * showToast({ message: 'Hello World!', persistent: false, variant: 'success' });
 * showToast({ message: 'Wow!', variant: 'error' });
 * showToast({
 *   message: 'Item added!',
 *   variant: 'success',
 *   actionLinks: [
 *     { href: '/cart', text: 'View Cart' },
 *     { href: '/checkout', text: 'Checkout', onClick: () => console.log('Checkout clicked') }
 *   ]
 * });
 */
export const useToaster = () => {
  const { toasterRef } = useMarketManagers();

  const showToast = ({
    message = '',
    persistent = false,
    variant = 'info',
    actionLinks,
  }: Toast) => {
    if (!toasterRef.current) {
      return;
    }

    const newToast = document.createElement('market-toast');
    newToast.variant = variant;
    newToast.textContent = message;
    newToast.persistent = persistent;

    // if action links are provided, create anchor elements and append in order
    if (actionLinks && actionLinks.length > 0) {
      actionLinks.forEach((actionLink) => {
        const link = document.createElement('a');
        link.href = actionLink.href;
        link.textContent = actionLink.text;

        if (actionLink.onClick) {
          link.addEventListener('click', actionLink.onClick);
        }

        newToast.appendChild(link);
      });
    }

    toasterRef.current.show(newToast);
  };

  return { showToast };
};
